import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Plus, HelpCircle, X } from 'lucide-react';
import { useI18nText } from '@/lib/utils/i18nText';
import { loadSuggestedTags, loadOldTags, loadRecentlyUsedTags, setPanelTags } from '@/lib/api/panel.api';
import { usePortalDropdown } from '@/lib/utils/usePortalDropdown';
import { InlineSpinner } from '@/components/Spinner';
import Tooltip from '@/components/Tooltip';
import { PopoverShell } from '@/components/PopoverShell';
import { RowActionButton } from './RowActionButton';
import ChannelTypeIcon from '@/components/icons/ChannelTypeIcon';
import { ChannelThumbnail } from '@/components/ChannelThumbnail';
import { buildTagKindMap, buildTagSuggestions, TagKindChip } from './tagSuggestions';
import type { Campaign, Influencer } from '../types';

/**
 * Self-contained "Edit tags" popover — renders its own trigger button (like
 * EmailCell/EstPriceCell) instead of the page lifting an `open`/`selected user`
 * state, so it can anchor to that button via the shared `PopoverShell` (Radix
 * Popover: collision-aware placement, focus trap, Esc/outside-click-to-close —
 * see PopoverShell.tsx) rather than the old fixed-center full-screen modal.
 *
 * Every add/remove persists immediately (no manual Save step). Edits are
 * serialized through `persistTags`/`flushSave` so a save already in flight
 * is followed by one more save with the latest tag set, instead of firing
 * a request per keystroke/click.
 */
export function TagEditPopover({
  user,
  campaigns,
  onSaved,
}: {
  user: Influencer;
  campaigns: Campaign[];
  onSaved: (newTags: string[]) => void;
}) {
  const { t } = useI18nText();
  const userId = user._id;
  const initialTags = useMemo(() => user.tags ?? [], [user.tags]);
  const userCampaignIds = useMemo(
    () => (user.campaigns ?? []).map((c) => c.campaignId ?? '').filter(Boolean),
    [user.campaigns],
  );
  const ch = user.channels?.[0];
  const displayName = user.profile?.fullname || user.profile?.name || ch?.title || t('Unknown', '알 수 없음');

  const [open, setOpen] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [suggestedTags, setSuggestedTags] = useState<string[]>([]);
  const [recentTags, setRecentTags] = useState<string[]>([]);
  const [oldTags, setOldTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  // Anchor the dropdown to the whole chip box (not the inner input) and render it
  // in a body portal so the popover's own overflow/edges can't clip it.
  const boxRef = useRef<HTMLDivElement>(null);
  const { pos, open: dropdownOpen, openDropdown, closeDropdown, position } = usePortalDropdown(boxRef, 'fixed');

  const lastSavedRef = useRef<string[]>([]);
  const pendingRef = useRef<string[] | null>(null);
  const savingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setTags([...initialTags]);
    lastSavedRef.current = [...initialTags];
    pendingRef.current = null;
    setInputValue('');
    setActiveIndex(-1);
    setLoading(true);
    // Land the cursor in the tag input and open the suggestion dropdown right away
    // so it shows a loading row now and the list as soon as the tags finish loading
    // (no manual re-focus needed).
    inputRef.current?.focus();
    openDropdown();

    Promise.all([
      loadSuggestedTags(userId)
        .then((res) => {
          const apiTags = res?.tags;
          if (Array.isArray(apiTags) && apiTags.length > 0) return apiTags;
          const fromCampaigns = campaigns
            .filter((c) => userCampaignIds.includes(c._id) || userCampaignIds.includes(c.id ?? ''))
            .map((c) => c.name?.trim())
            .filter((n): n is string => !!n);
          return [...new Set(fromCampaigns)];
        })
        .catch(() => {
          const fromCampaigns = campaigns
            .filter((c) => userCampaignIds.includes(c._id) || userCampaignIds.includes(c.id ?? ''))
            .map((c) => c.name?.trim())
            .filter((n): n is string => !!n);
          return [...new Set(fromCampaigns)];
        }),
      loadRecentlyUsedTags().catch(() => []),
      loadOldTags().catch(() => []),
    ]).then(([suggested, recent, old]) => {
      setSuggestedTags(suggested);
      setRecentTags(recent);
      setOldTags(old);
    }).finally(() => setLoading(false));
  }, [open, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reposition the portal dropdown after the popover's content settles — once the
  // suggestions finish loading (or chips wrap to a new line) it grows, moving the
  // anchor box. useLayoutEffect runs before paint, so there's no visible jump.
  useLayoutEffect(() => {
    if (dropdownOpen) openDropdown();
  }, [loading, tags.length, dropdownOpen, openDropdown]);

  // clean oldTags: trim and remove empty/whitespace-only entries
  const cleanOldTags = useMemo(
    () => oldTags.map((t) => t.trim()).filter(Boolean),
    [oldTags],
  );

  // Classify each suggestion as a campaign name, roster name (status === 'roster'),
  // or plain tag. The BE syncs each roster/campaign name as a tag value, so these
  // are the meaningful autocomplete sources alongside previously-used tags.
  const kindMap = useMemo(() => buildTagKindMap(campaigns), [campaigns]);

  // Names of the campaigns/rosters this influencer belongs to — surfaced first
  // while typing so short queries lead with the most relevant matches.
  const ownNames = useMemo(() => {
    const set = new Set<string>();
    for (const c of campaigns) {
      const name = c.name?.trim();
      if (name && (userCampaignIds.includes(c._id) || userCampaignIds.includes(c.id ?? ''))) set.add(name);
    }
    return set;
  }, [campaigns, userCampaignIds]);

  // autocomplete options:
  // - typing: campaign/roster name matches first (own first), then other used tags
  // - empty input + focused: recently-used tags, then the full previously-used pool
  const autocompleteOptions = useMemo(() => {
    if (!dropdownOpen) return [];
    return buildTagSuggestions({
      query: inputValue,
      kindMap,
      ownNames,
      recentTags,
      oldTags: cleanOldTags,
      exclude: tags,
      limit: 8,
    });
  }, [dropdownOpen, inputValue, kindMap, ownNames, cleanOldTags, recentTags, tags]);

  const flushSave = useCallback(async () => {
    if (savingRef.current) return;
    const toSave = pendingRef.current;
    if (toSave === null) return;
    pendingRef.current = null;
    savingRef.current = true;
    setSaving(true);
    try {
      await setPanelTags(userId, toSave);
      lastSavedRef.current = toSave;
      onSaved(toSave);
    } catch {
      // global error handler already surfaced the failure; drop the
      // unsaved change so displayed tags don't drift from the server
      pendingRef.current = null;
      setTags(lastSavedRef.current);
    } finally {
      savingRef.current = false;
      setSaving(false);
      if (pendingRef.current !== null) flushSave();
    }
  }, [userId, onSaved]);

  const persistTags = useCallback((next: string[]) => {
    pendingRef.current = next;
    flushSave();
  }, [flushSave]);

  const addTag = (tag: string) => {
    const next = tag.trim();
    if (!next) return;
    setTags((prev) => {
      if (prev.includes(next)) return prev;
      const updated = [...prev, next];
      persistTags(updated);
      return updated;
    });
  };

  const removeTag = (tag: string) => {
    setTags((prev) => {
      if (!prev.includes(tag)) return prev;
      const updated = prev.filter((x) => x !== tag);
      persistTags(updated);
      return updated;
    });
  };

  const commitInputOrAutocomplete = () => {
    if (activeIndex >= 0 && autocompleteOptions[activeIndex]) {
      addTag(autocompleteOptions[activeIndex].value);
    } else if (inputValue.trim()) {
      addTag(inputValue.trim());
    }
    setInputValue('');
    closeDropdown();
    setActiveIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      commitInputOrAutocomplete();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, autocompleteOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Escape') {
      closeDropdown();
      setActiveIndex(-1);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      // Commit whatever's still typed instead of silently dropping it on close.
      const pending = inputValue.trim();
      if (pending) {
        addTag(pending);
        setInputValue('');
      }
    }
    setOpen(next);
  };

  const autocompleteHelp = t(
    'Start typing to search campaign and roster names, or click to see recent picks. Press Enter or Tab to add.',
    '입력하면 캠페인·로스터 이름에서 자동완성됩니다. 클릭 시 최근 사용 태그가 표시됩니다. Enter 또는 Tab으로 추가할 수 있습니다.',
  );

  return (
    <PopoverShell
      open={open}
      onOpenChange={handleOpenChange}
      trigger={<RowActionButton>{t('Edit', '편집')}</RowActionButton>}
      side="bottom"
      align="start"
      width={440}
      maxHeight="var(--radix-popper-available-height, 520px)"
      resizable
    >
      {/* height (not just maxHeight) also inherited so the body below can actually grow
          into whatever extra room a corner-drag resize adds — see PopoverShell's
          `resizable` doc comment. */}
      <div className="flex flex-col" style={{ maxHeight: 'inherit', height: 'inherit' }}>
        {/* Identity header — same thumbnail + platform icon + name + close (✕)
            treatment as every other channel-row popover (EstPriceCell's
            Channel Rates, CampaignHistoryPopover). */}
        <div className="est-price-popover-header">
          <ChannelThumbnail src={ch?.thumbnail} />
          <ChannelTypeIcon type={ch?.type ?? 'youtube'} size={14} asBadge />
          <span className="est-price-popover-name">{displayName}</span>
          <Tooltip content={autocompleteHelp} placement="left">
            <span className="flex cursor-help items-center text-ggc-text-gray">
              <HelpCircle className="h-3.5 w-3.5" />
            </span>
          </Tooltip>
          {saving && (
            <span className="flex items-center gap-1 ggc-text-caption-s text-ggc-text-gray">
              <InlineSpinner size={12} />
            </span>
          )}
          <button
            type="button"
            className="est-price-popover-close"
            onClick={() => handleOpenChange(false)}
            aria-label={t('Close', '닫기')}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="overflow-y-auto p-3">
          {/* tag chips + input with autocomplete */}
          <div className="mb-3">
            <div
              ref={boxRef}
              className="flex flex-wrap items-center gap-1 rounded-ggc-md border border-ggc-border-gray bg-white px-3 py-2 transition-colors focus-within:border-ggc-blue focus-within:ring-2 focus-within:ring-ggc-blue/10"
            >
              {tags.map((tag) => (
                <span key={tag} className="inline-flex items-center gap-1 rounded-ggc-sm bg-ggc-bg-base px-2 py-0.5 text-xs">
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)} className="text-ggc-disabled hover:text-ggc-text-primary">×</button>
                </span>
              ))}
              <input
                ref={inputRef}
                className="min-w-[120px] flex-1 border-none px-1 py-0.5 text-sm outline-none"
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  setActiveIndex(-1);
                  openDropdown();
                }}
                onFocus={() => openDropdown()}
                onBlur={() => {
                  // delay so mousedown on dropdown option fires first
                  setTimeout(closeDropdown, 150);
                }}
                onKeyDown={handleKeyDown}
                placeholder={t('Type and press Enter or Tab...', '입력 후 Enter 또는 Tab')}
              />
            </div>

            {/* autocomplete dropdown — portaled so the popover's own edges can't clip it */}
            {dropdownOpen && pos && (loading || autocompleteOptions.length > 0) && createPortal(
              <div
                style={{ position, top: pos.top, left: pos.left, width: pos.width, zIndex: 9999 }}
                className="max-h-52 overflow-y-auto rounded-ggc-sm border border-ggc-border-gray-dark bg-white shadow-ggc-md"
              >
                {autocompleteOptions.map((option, idx) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                      idx === activeIndex
                        ? 'bg-ggc-bg-surface text-ggc-cta-blue'
                        : 'text-ggc-text-primary hover:bg-ggc-bg-surface'
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault(); // prevent input blur before click
                      addTag(option.value);
                      setInputValue('');
                      closeDropdown();
                      setActiveIndex(-1);
                      inputRef.current?.focus();
                    }}
                  >
                    <TagKindChip kind={option.kind} />
                    <span className="truncate">{option.value}</span>
                  </button>
                ))}
                {loading && (
                  <div className="flex items-center gap-2 px-3 py-2 text-sm text-ggc-text-gray">
                    <InlineSpinner size={14} />
                    {t('Loading suggestions...', '추천 태그 불러오는 중...')}
                  </div>
                )}
              </div>,
              document.body,
            )}
          </div>

          {/* Fixed-height region so the popover doesn't jitter-resize when the
              suggestions finish loading. Taller content scrolls in place. */}
          <div className="h-[260px] overflow-y-auto">
          {loading ? (
            <div className="flex h-full items-center justify-center"><InlineSpinner /></div>
          ) : (
            <>
              {/* suggested tags based on campaigns */}
              {suggestedTags.length > 0 && (
                <div className="mb-4">
                  <h4 className="mb-0.5 ggc-text-section-header text-ggc-cta-blue">
                    {t('Suggested tags based on campaigns', '캠페인 기반 추천 태그')}
                  </h4>
                  <p className="mb-2 ggc-text-caption-s text-ggc-text-gray">
                    {t(
                      'Tags derived from campaigns this influencer has participated in.',
                      '이 인플루언서가 참여한 캠페인에서 자동으로 추출된 태그입니다.',
                    )}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestedTags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => addTag(tag)}
                        className={`inline-flex items-center gap-1 rounded-ggc-sm border px-2 py-0.5 text-xs transition ${
                          tags.includes(tag)
                            ? 'border-ggc-green bg-ggc-green-light text-ggc-green'
                            : 'border-ggc-border-gray-dark bg-white text-ggc-text-gray hover:border-ggc-cta-blue'
                        }`}
                      >
                        <Plus className="h-3 w-3" />{tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* recently used tags */}
              {recentTags.length > 0 && (
                <div>
                  <h4 className="mb-0.5 ggc-text-section-header">
                    {t('Recently used tags', '최근 사용 태그')}
                  </h4>
                  <p className="mb-2 ggc-text-caption-s text-ggc-text-gray">
                    {t(
                      'Tags you have recently added to other influencers. Click to apply.',
                      '최근 다른 인플루언서에 추가한 태그입니다. 클릭하면 바로 적용됩니다.',
                    )}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {recentTags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => addTag(tag)}
                        className={`inline-flex items-center gap-1 rounded-ggc-sm border px-2 py-0.5 text-xs transition ${
                          tags.includes(tag)
                            ? 'border-ggc-green bg-ggc-green-light text-ggc-green'
                            : 'border-ggc-border-gray-dark bg-white text-ggc-text-gray hover:border-ggc-cta-blue'
                        }`}
                      >
                        <Plus className="h-3 w-3" />{tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {suggestedTags.length === 0 && recentTags.length === 0 && (
                <span className="text-xs text-ggc-disabled">{t('No suggested tags', '추천 태그 없음')}</span>
              )}
            </>
          )}
          </div>
        </div>
      </div>
    </PopoverShell>
  );
}
